import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import plusIcon from "../../../assets/img/icons/plus_white.svg";
import {
  ButtonIcon,
  ColoredButtonWithIcon,
} from "../../../styledComponents/buttons";
import {
  CommonFlex,
  CommonGrid,
  CommonText,
  FormAlert,
  InputLabel,
  InputWithLabelSection,
} from "../../../styledComponents/common";
import { currencySymbolMap } from "../../../utils/currencies";
import LineItemModal from "../../JobComponents/LineItemModal";
import LineItemsTable from "../../JobComponents/LineItemsTable";
import { CommonCardContainer } from "components/DashboardCards/cardStyles";
import {
  GrandTotalAmount,
  GrandTotalAmountSection,
  GrandTotalContainer,
  GrandTotalLabel,
  GrandTotalLabelSection,
  TaxAmountLabel,
} from "../invoiceStyles";
import TaxAmountSection from "./TaxAmountSection";
import { InputField } from "../../../styledComponents/common";

const InvoiceLineItemSectionEdit = props => {
  const {
    currency,
    createInvoiceItem,
    updateInvoiceItem,
    deleteInvoiceItem,
    updateInvoiceTax,
    updateInvoiceDiscount,
    invoiceDetails,
    selectedOrganization,
  } = props;

  const { t } = useTranslation();
  const [action, setAction] = useState("add");
  const [popupOpen, setPopupOpen] = useState(false);
  const [dataForEdit, setDataForEdit] = useState(null);
  const initialLoadRef = useRef(false);

  const [itemArray, setItemArray] = useState([]);
  const [totalCalc, setTotalCalc] = useState(0);
  const [tax1, setTax1] = useState({
    name: "",
    value: "",
    percent: true,
    isChanged: false,
  });
  const [tax2, setTax2] = useState({
    name: "",
    value: "",
    percent: true,
    isChanged: false,
  });
  const [discount, setDiscount] = useState({
    value: "",
    percent: true,
    isChanged: false,
  });
  const [grandTotalValue, setGrandTotalValue] = useState(0);
  const [saveErrors, setSaveErrors] = useState(null);

  // Load tax and discount info on initial load
  useEffect(() => {
    if (invoiceDetails && !initialLoadRef.current) {
      initialLoadRef.current = true;
      if (invoiceDetails.tax1) {
        setTax1({
          name: invoiceDetails.tax1.name || "",
          value:
            invoiceDetails.tax1.type === "percent"
              ? invoiceDetails.tax1.percent_amount
              : invoiceDetails.tax1.fixed_amount,
          percent: invoiceDetails.tax1.type === "percent",
          id: invoiceDetails.tax1.id,
          isChanged: false,
        });
      }
      if (invoiceDetails.tax2) {
        setTax2({
          name: invoiceDetails.tax2.name || "",
          value:
            invoiceDetails.tax2.type === "percent"
              ? invoiceDetails.tax2.percent_amount
              : invoiceDetails.tax2.fixed_amount,
          percent: invoiceDetails.tax2.type === "percent",
          id: invoiceDetails.tax2.id,
          isChanged: false,
        });
      }
      if (invoiceDetails.discount) {
        setDiscount({
          value:
            invoiceDetails.discount.type === "percent"
              ? invoiceDetails.discount.percent_amount
              : invoiceDetails.discount.fixed_amount,
          percent: invoiceDetails.discount.type === "percent",
          id: invoiceDetails.discount.id,
          isChanged: false,
        });
      }
    }
  }, [invoiceDetails]);

  // Load items from invoiceDetails
  useEffect(() => {
    if (invoiceDetails?.items && invoiceDetails.items.length > 0) {
      const loadedItems = invoiceDetails.items.map(item => ({
        id: item.id,
        itemType: item.type === "service" ? "Service" : "Product",
        name: item.name || "",
        description: item.description || "",
        quantity: item.quantity,
        unitPrice: item.unit_price,
        amount: item.quantity * item.unit_price,
        tax1Toggle: item.tax1,
        tax2Toggle: item.tax2,
        evidenceToggle: false,
      }));
      setItemArray(loadedItems);
    }
  }, [invoiceDetails?.items]);

  // Calculate total when items change
  useEffect(() => {
    totalCalculation();
  }, [itemArray]);

  // Calculate grand total with specific value dependencies (not object references)
  useEffect(() => {
    grandTotalCalculation();
  }, [itemArray, tax1.value, tax1.percent, tax2.value, tax2.percent, discount.value, discount.percent, totalCalc]);

  const handleDeleteItem = index => {
    const item = itemArray[index];

    // Call API to delete if item has an ID
    if (item?.id && deleteInvoiceItem) {
      const payload = {
        organization_id: selectedOrganization?.id,
        invoice_id: invoiceDetails?.id,
        item_id: item.id,
      };
      deleteInvoiceItem(payload);
    }

    // Remove from local state
    let newArray = [...itemArray];
    newArray.splice(index, 1);
    setItemArray(newArray);
  };

  const totalCalculation = () => {
    if (itemArray && itemArray.length) {
      let total = 0;
      itemArray.forEach(item => {
        total += item.quantity * item.unitPrice;
      });
      setTotalCalc(total.toFixed(2));
    } else {
      setTotalCalc(0);
    }
  };

  const handleEditItem = index => {
    let item = itemArray[index];
    setDataForEdit({ ...item, index });
    setAction("edit");
    setPopupOpen(true);
  };

  const handleAddItems = items => {
    // Handle Edit Case
    if (items.action === "edit") {
      // Call API to update item
      if (items.id && updateInvoiceItem) {
        const payload = {
          organization_id: selectedOrganization?.id,
          invoice_id: invoiceDetails?.id,
          item_id: items.id,
          type: items.itemType?.toLowerCase(),
          name: items.name,
          description: items.description,
          quantity: items.quantity,
          unit_price: items.unitPrice,
          allow_evidences: items.evidenceToggle,
          name: items.name,
          tax1: items.tax1Toggle,
          tax2: items.tax2Toggle,
        };
        updateInvoiceItem(payload);
      }

      let newArray = [...itemArray];
      newArray[items.index] = {
        itemType: items.itemType,
        name: items.name,
        description: items.description,
        quantity: items.quantity,
        unitPrice: items.unitPrice,
        amount: items.amount,
        evidenceToggle: items.evidenceToggle,
        tax1Toggle: items.tax1Toggle,
        tax2Toggle: items.tax2Toggle,
        id: items.id,
      };
      setItemArray(newArray);
      return;
    }

    // Handle Add Case - Call API to create item
    if (createInvoiceItem) {
      const payload = {
        invoice_id: invoiceDetails?.id,
        type: items.itemType?.toLowerCase(),
        name: items.name,
        description: items.description,
        quantity: items.quantity,
        unit_price: items.unitPrice,
        allow_evidences: items.evidenceToggle,
        name: items.name,
        organization_id: selectedOrganization?.id,
      };
      createInvoiceItem(payload);
    }

    // Add to local state
    let newArray = [...itemArray, items];
    setItemArray(newArray);
  };

  const handleTax1ToggleClick = index => {
    let newArr = [...itemArray];
    newArr[index].tax1Toggle = !newArr[index].tax1Toggle;

    // Call API to update item
    if (newArr[index]?.id && updateInvoiceItem) {
      const item = newArr[index];
      const payload = {
        organization_id: selectedOrganization?.id,
        invoice_id: invoiceDetails?.id,
        item_id: item.id,
        type: item.itemType?.toLowerCase(),
        name: item.name,
        description: item.description,
        quantity: item.quantity,
        unit_price: item.unitPrice,
        allow_evidences: item.evidenceToggle,
        tax1: item.tax1Toggle,
        tax2: item.tax2Toggle,
        name: item.name,
      };
      updateInvoiceItem(payload);
    }

    setItemArray(newArr);
  };

  const handleTax2ToggleClick = index => {
    let newArr = [...itemArray];
    newArr[index].tax2Toggle = !newArr[index].tax2Toggle;

    // Call API to update item
    if (newArr[index]?.id && updateInvoiceItem) {
      const item = newArr[index];
      const payload = {
        organization_id: selectedOrganization?.id,
        invoice_id: invoiceDetails?.id,
        item_id: item.id,
        type: item.itemType?.toLowerCase(),
        name: item.name,
        description: item.description,
        quantity: item.quantity,
        unit_price: item.unitPrice,
        allow_evidences: item.evidenceToggle,
        tax1: item.tax1Toggle,
        tax2: item.tax2Toggle,
        name: item.name,
      };
      updateInvoiceItem(payload);
    }

    setItemArray(newArr);
  };

  const onTax1NameChange = e => {
    const updated = { ...tax1, name: e.target.value, isChanged: true };
    setTax1(updated);
  };

  const onTax1ValueChange = e => {
    let errors = { ...saveErrors };
    delete errors[`tax1Value`];
    setSaveErrors(errors);

    const updated = { ...tax1, value: e.target.value, isChanged: true };
    setTax1(updated);
  };

  const toggleTax1CalcType = value => {
    const updated = {
      ...tax1,
      percent: value,
      isChanged: true,
    };
    setTax1(updated);
  };

  const onTax2NameChange = e => {
    const updated = { ...tax2, name: e.target.value, isChanged: true };
    setTax2(updated);
  };

  const onTax2ValueChange = e => {
    let errors = { ...saveErrors };
    delete errors[`tax2Value`];
    setSaveErrors(errors);

    const updated = { ...tax2, value: e.target.value, isChanged: true };
    setTax2(updated);
  };

  const toggleTax2CalcType = value => {
    const updated = {
      ...tax2,
      percent: value,
      isChanged: true,
    };
    setTax2(updated);
  };

  const onDiscountValueChange = e => {
    let errors = { ...saveErrors };
    delete errors[`discountValue`];
    delete errors[`discountFixed`];
    setSaveErrors(errors);

    const updated = { ...discount, value: e.target.value, isChanged: true };
    setDiscount(updated);
  };

  const toggleDiscountCalcType = value => {
    const updated = {
      ...discount,
      percent: value,
      isChanged: true,
    };
    setDiscount(updated);
  };

  const grandTotalCalculation = () => {
    const calculateTaxDiscount = (object, cost) => {
      if (object.percent) {
        return (object.value * cost) / 100;
      } else if (object.value) {
        return object.value;
      } else {
        return 0;
      }
    };

    if (itemArray && itemArray.length) {
      let totalTax = 0;
      itemArray.forEach((item, index) => {
        let itemCost = item.quantity * item.unitPrice;
        if (item.tax1Toggle) {
          totalTax += parseFloat(calculateTaxDiscount(tax1, itemCost));
        }
        if (item.tax2Toggle) {
          totalTax += parseFloat(calculateTaxDiscount(tax2, itemCost));
        }
      });

      let grandTotal =
        parseFloat(totalCalc) +
        totalTax -
        parseFloat(calculateTaxDiscount(discount, totalCalc));
      setGrandTotalValue(grandTotal.toFixed(2));
    }
  };

  const handleAddItemClick = () => {
    setAction("add");
    setDataForEdit(null);
    setPopupOpen(true);
  };

  const handleSaveTaxDiscount = () => {
    // Save tax1 if changed
    if (tax1.isChanged && updateInvoiceTax) {
      const payload = {
        organization_id: selectedOrganization?.id,
        invoice_id: invoiceDetails?.id,
        tax_item_id: invoiceDetails?.tax1?.id,
        tax_field: "tax1",
        name: tax1.name,
        type: tax1.percent ? "percent" : "fixed",
        ...(tax1.percent
          ? { percent_amount: tax1.value }
          : { fixed_amount: tax1.value }),
      };
      updateInvoiceTax(payload);
      setTax1({ ...tax1, isChanged: false });
    }

    // Save tax2 if changed
    if (tax2.isChanged && updateInvoiceTax) {
      const payload = {
        organization_id: selectedOrganization?.id,
        invoice_id: invoiceDetails?.id,
        tax_item_id: invoiceDetails?.tax2?.id,
        tax_field: "tax2",
        name: tax2.name,
        type: tax2.percent ? "percent" : "fixed",
        ...(tax2.percent
          ? { percent_amount: tax2.value }
          : { fixed_amount: tax2.value }),
      };
      updateInvoiceTax(payload);
      setTax2({ ...tax2, isChanged: false });
    }

    // Save discount if changed
    if (discount.isChanged && updateInvoiceDiscount) {
      const payload = {
        organization_id: selectedOrganization?.id,
        invoice_id: invoiceDetails?.id,
        discount_id: invoiceDetails?.discount?.id,
        discount: {
          type: discount.percent ? "percent" : "fixed",
          ...(discount.percent
            ? { percent_amount: discount.value }
            : { fixed_amount: discount.value }),
        },
      };
      updateInvoiceDiscount(payload);
      setDiscount({ ...discount, isChanged: false });
    }
  };

  return (
    <CommonCardContainer style={{ minWidth: '1200px', marginTop: "6px" }} bgColor="#EDF8FE">
      <LineItemModal
        isOpen={popupOpen}
        toggle={() => setPopupOpen(!popupOpen)}
        addItems={handleAddItems}
        action={action}
        dataForEdit={dataForEdit}
        currency={currency}
        pageType="invoice"
      />
      <CommonFlex disableResponsive margin="0 0 16px 0" justifyContent="space-between" alignItems="center">
        <CommonText name title fontSize="16px">
          Line Items
        </CommonText>
        <ColoredButtonWithIcon
          type="orange"
          onClick={() => handleAddItemClick()}
        >
          <ButtonIcon src={plusIcon} alt="plus" />
          Add Line Items
        </ColoredButtonWithIcon>
      </CommonFlex>

      <div>
        {itemArray && itemArray.length > 0 ? (
          <LineItemsTable
            itemArray={itemArray}
            onEditItem={handleEditItem}
            onDeleteItem={handleDeleteItem}
            onTax1ToggleClick={handleTax1ToggleClick}
            onTax2ToggleClick={handleTax2ToggleClick}
            totalCalc={totalCalc}
            currency={currency}
            pageType="invoice"
          />
        ) : (
          <CommonText>No line items added yet</CommonText>
        )}
      </div>

      <CommonFlex disableResponsive justifyContent="flex-end" style={{ marginTop: "20px" }} alignItems="center">
        <CommonFlex disableResponsive width="418px" direction="column" alignItems="flex-end" gap="16px">
          <CommonGrid>
            <InputWithLabelSection>
              <TaxAmountLabel>Tax 1 Name</TaxAmountLabel>
              <InputField
                type="text"
                placeholder="Enter Tax 1 Name"
                value={tax1.name}
                onChange={e => onTax1NameChange(e)}
              />
            </InputWithLabelSection>
            <TaxAmountSection
              value={tax1.value}
              onChange={e => onTax1ValueChange(e)}
              isPercentage={tax1.percent}
              onTypeChange={isPercent => toggleTax1CalcType(isPercent)}
              currency={currency}
            />
          </CommonGrid>
          <CommonGrid disableResponsive>
            <InputWithLabelSection>
              <TaxAmountLabel>Tax 2 Name</TaxAmountLabel>
              <InputField
                type="text"
                placeholder="Enter Tax 2 Name"
                value={tax2.name}
                onChange={e => onTax2NameChange(e)}
              />
            </InputWithLabelSection>
            <TaxAmountSection
              value={tax2.value}
              onChange={e => onTax2ValueChange(e)}
              isPercentage={tax2.percent}
              onTypeChange={isPercent => toggleTax2CalcType(isPercent)}
              currency={currency}
            />
          </CommonGrid>
          <TaxAmountSection
            label="Discount"
            value={discount.value}
            onChange={e => onDiscountValueChange(e)}
            isPercentage={discount.percent}
            onTypeChange={isPercent => toggleDiscountCalcType(isPercent)}
            currency={currency}
          />
          <GrandTotalContainer>
            <GrandTotalLabelSection>
              <GrandTotalLabel>Grand Total</GrandTotalLabel>
            </GrandTotalLabelSection>
            <GrandTotalAmountSection>
              <GrandTotalAmount>
                {currency && currencySymbolMap[currency] ? currencySymbolMap[currency] : '$'}
                {grandTotalValue || "0.00"}
              </GrandTotalAmount>
            </GrandTotalAmountSection>
          </GrandTotalContainer>

          {(tax1.isChanged || tax2.isChanged || discount.isChanged) && (
            <div style={{ marginRight: `auto`, marginTop: "10px" }}>
              <ColoredButtonWithIcon
                type="orange"
                onClick={handleSaveTaxDiscount}
              >
                Save
              </ColoredButtonWithIcon>
            </div>
          )}
        </CommonFlex>
      </CommonFlex>
    </CommonCardContainer>
  );
};

export default InvoiceLineItemSectionEdit;
